/**
 * SEARCH-001 — l'appel à `GET /search/global` et la forme de sa réponse, partagés par la
 * palette ⌘K et le sélecteur « Fiche liée ».
 *
 * Les deux lisent le MÊME `queryKey` (`queryKeys.search.global`) : le cache React Query
 * est commun, donc la donnée mise en cache doit avoir la même forme des deux côtés. Un
 * seul appel ici, sinon l'un lirait la donnée rangée par l'autre sous une autre forme.
 */

import { API_BASE, apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import type { ApiHit } from './searchPaletteHelpers'

/** Réponse du serveur (`GlobalSearchResponse`, `backend/src/api/_search_schemas.py`). */
export interface GlobalSearchResponse {
    results: ApiHit[]
    /**
     * `true` seulement quand le serveur a abandonné la correction des fautes faute de
     * temps : les résultats rendus sont alors ceux trouvés SANS faute, et il en manque
     * peut-être. Absent d'un serveur qui ne le porte pas encore : lu comme `false`.
     */
    typos_skipped?: boolean
}

/**
 * Sous ce nombre de lettres ou chiffres, le serveur ne cherche rien (SEARCH-001 §7) : à 2
 * lettres, aucun index ne sert la recherche (mesuré à 50 000 fiches, au-delà du délai d'une
 * frappe). Les espaces et la ponctuation ne comptent pas : « e e » et « d'a » en ont 2.
 */
export const SAISIE_MIN = 3

/**
 * Le serveur cherchera-t-il cette saisie ? Dès 3 lettres ou chiffres ; en deçà, seulement un
 * IDENTIFIANT d'au moins 2 caractères (un @, ou un seul bloc qui porte un point ou un
 * chiffre), lu au début du texte. Jumeau de `global_search` (`_MIN_SAISIE`, `est_identifiant`,
 * `backend/src/infra/search_repository/`) : l'écran n'envoie pas une saisie que le serveur
 * rendrait vide, et dit pourquoi.
 */
export function saisieCherchable(q: string): boolean {
    const s = q.trim()
    const significatifs = s.match(/[\p{L}\p{N}]/gu)?.length ?? 0
    if (significatifs >= SAISIE_MIN) return true
    const identifiant = s.includes('@') || (!/\s/.test(s) && /[.0-9]/.test(s))
    return significatifs > 0 && s.length >= 2 && identifiant
}

/** Ce que l'écran garde d'une recherche. */
export interface RechercheGlobale {
    results: ApiHit[]
    typosSkipped: boolean
}

export async function chercherPartout({
    q,
    entityId,
    types,
    signal,
}: {
    q: string
    entityId: string | null
    types: readonly string[]
    signal?: AbortSignal
}): Promise<RechercheGlobale> {
    const params = new URLSearchParams({ q, entity_id: entityId ?? '', types: types.join(',') })
    const res = await apiFetch(`${API_BASE}/search/global?${params.toString()}`, {
        headers: entityId ? { 'X-Entity-Id': entityId } : undefined,
        signal,
    })
    if (!res.ok) await handleApiError(res)
    const body = (await res.json()) as GlobalSearchResponse
    return { results: body.results, typosSkipped: body.typos_skipped === true }
}
